import dayjs from "dayjs";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowLeft, CheckCircle2, Eye, Loader2, Lock } from "lucide-react";

import FormAnswer from "@/components/form/form-answer/form-answer";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useFormStore } from "@/store";
import { useFormResponseStore } from "@/store/form/use-form-response-store";
import {
  useMyDelegation,
  useSaveMyDelegation,
  useSubmitMyDelegation,
} from "@/hooks";
import type { DelegationSectionResponse, DelegationView } from "@/types";

const AUTOSAVE_DEBOUNCE_MS = 2000;

const toStoreShape = (sections: DelegationSectionResponse[] | null) => {
  const shaped: Record<string, any> = {};
  (sections ?? []).forEach((section) => {
    if (!section?.sectionId) return;
    shaped[section.sectionId] = {
      sectionId: section.sectionId,
      sectionName: section.sectionName,
      questions: Object.fromEntries(
        (section.responses ?? []).map((a) => [a.questionId, a])
      ),
    };
  });
  return shaped;
};

/**
 * The in-app counterpart of the public delegation page, for recipients who have
 * an account. Access is the session, not a token, so it calls the `mine`
 * endpoints and — being authenticated — lets the shared form components (upload,
 * dropdowns) use the normal API without the token shim.
 */
const MyDelegationFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();

  const { setFormId, setSections } = useFormStore();
  const { formResponses, hydrateResponses, resetResponses } =
    useFormResponseStore();
  const { data, isLoading, isError } = useMyDelegation(id);
  const save = useSaveMyDelegation(id);
  const submit = useSubmitMyDelegation(id);

  const view: DelegationView | undefined = data;
  const canEdit = !!view?.canEdit;
  const sender = view?.sentBy.name || view?.sentBy.email || "";

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [, setCurrentSectionId] = useState<string | null>(null);
  const hydrated = useRef(false);

  useEffect(() => {
    if (!view?.form || hydrated.current) return;
    hydrated.current = true;
    setFormId(view.form.id);
    setSections(view.form.design ?? []);
    hydrateResponses(toStoreShape(view.responses));
  }, [view]);

  useEffect(
    () => () => {
      resetResponses();
      setSections([]);
    },
    []
  );

  const transformed = useMemo(
    () =>
      Object.values(formResponses).map((section: any) => ({
        sectionId: section.sectionId,
        sectionName: section.sectionName,
        responses: Object.values(section.questions),
      })),
    [formResponses]
  );

  const dirty = useRef(false);
  useEffect(() => {
    if (!canEdit || Object.keys(formResponses).length === 0) return;
    if (!dirty.current) {
      dirty.current = true;
      return;
    }
    const timer = setTimeout(() => {
      save
        .mutateAsync(transformed)
        .then((r) => setSavedAt(r?.savedAt ?? new Date().toISOString()))
        .catch(() => undefined);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [canEdit, transformed]);

  const handleSubmit = async () => {
    try {
      const r = await submit.mutateAsync(transformed);
      if (!r?.success) throw new Error();
      setConfirmOpen(false);
      toast.success(t("delegation.submitted"));
      navigate("/delegated-to-me");
    } catch {
      toast.error(t("delegation.submitFailed"));
    }
  };

  if (isLoading) {
    return (
      <Centered>
        <Loader2 className="h-5 w-5 animate-spin" /> {t("delegation.loading")}
      </Centered>
    );
  }

  if (isError || !view) {
    return (
      <Notice
        icon={<AlertCircle className="h-6 w-6 text-red-600" />}
        title={t("delegation.invalidLink")}
        body={t("delegation.invalidLinkHelp")}
      />
    );
  }

  if (view.status !== "PENDING" || !view.form) {
    return (
      <Notice
        icon={<CheckCircle2 className="h-6 w-6 text-darkBlue" />}
        title={
          view.status === "SUBMITTED"
            ? t("delegation.statusSubmittedTitle")
            : view.status === "APPROVED"
              ? t("delegation.statusApprovedTitle")
              : t("delegation.statusClosedTitle")
        }
        body={
          view.status === "SUBMITTED"
            ? t("delegation.statusSubmittedBody", {
                date: dayjs(view.submittedAt ?? undefined).format("MMM D, YYYY"),
                name: sender,
              })
            : view.status === "APPROVED"
              ? t("delegation.statusApprovedBody", { name: sender })
              : view.closedReason
                ? t(`delegation.closed${view.closedReason}`, { name: sender })
                : t("delegation.closedFallback")
        }
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl p-4">
      <button
        type="button"
        onClick={() => navigate("/delegated-to-me")}
        className="mb-3 flex items-center gap-1 text-sm text-gray-500"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("delegation.mine.back")}
      </button>

      <Card className="mb-4">
        <CardContent className="space-y-2 py-4 text-sm">
          <div className="text-lg font-semibold text-gray-900">
            {view.form.name}
          </div>
          <p className="text-gray-600">
            {t("delegation.sentBy", { name: sender })}
          </p>
          <p className="flex items-center gap-2 font-medium text-darkBlue">
            {canEdit ? (
              t("delegation.youAreEditor")
            ) : (
              <>
                <Eye className="h-4 w-4" />
                {t("delegation.youAreViewer")}
              </>
            )}
          </p>
          {view.message && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                {t("delegation.theirNote")}
              </div>
              <p className="whitespace-pre-wrap text-gray-800">{view.message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div
        className={canEdit ? undefined : "pointer-events-none select-none opacity-80"}
        aria-disabled={!canEdit}
      >
        <FormAnswer setCurrentFormSectionId={setCurrentSectionId} />
      </div>

      {canEdit ? (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="rounded border border-darkBlue px-4 py-2 text-sm text-darkBlue disabled:opacity-60"
            onClick={() =>
              save
                .mutateAsync(transformed)
                .then((r) => {
                  setSavedAt(r?.savedAt ?? new Date().toISOString());
                  toast.success(
                    t("delegation.saved", { time: dayjs().format("h:mm A") })
                  );
                })
                .catch(() => toast.error(t("delegation.saveFailed")))
            }
            disabled={save.isPending}
          >
            {save.isPending ? t("delegation.saving") : t("delegation.save")}
          </button>
          <button
            type="button"
            className="main-dark-button text-sm"
            onClick={() => setConfirmOpen(true)}
            disabled={submit.isPending}
          >
            {t("delegation.submit")}
          </button>
          {savedAt && (
            <span className="text-xs text-gray-500">
              {t("delegation.saved", { time: dayjs(savedAt).format("h:mm A") })}
            </span>
          )}
        </div>
      ) : (
        <p className="mt-6 flex items-center gap-2 text-sm text-gray-500">
          <Lock className="h-4 w-4" />
          {t("delegation.viewerReadOnly")}
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-[520px] sm:rounded-md">
          <DialogHeader>
            <DialogTitle>{t("delegation.submitConfirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("delegation.submitConfirmBody", { name: sender })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              onClick={() => setConfirmOpen(false)}
            >
              {t("delegation.cancel")}
            </button>
            <button
              type="button"
              className="main-dark-button text-sm"
              onClick={handleSubmit}
              disabled={submit.isPending}
            >
              {submit.isPending
                ? t("delegation.submitting")
                : t("delegation.confirm")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-gray-600">
    {children}
  </div>
);

const Notice: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <div className="mx-auto w-full max-w-2xl p-4">
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        {icon}
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
        <p className="max-w-md text-sm text-gray-600">{body}</p>
      </CardContent>
    </Card>
  </div>
);

export default MyDelegationFormPage;
